// Global Instructions Rule Applied!

import { useCallback, useEffect, useRef, useState } from 'react'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { AccountTopicsModel } from '../../../state_manager/data_models/account_topics_grid_model.ts'
import { AccountTopicsSavePayload } from '../../../state_manager/interfaces/distribution_lists.ts'
import { fetchAccountTopics, saveAccountTopics } from '../services/distributionListsService.ts'

/**
 * useAccountTopics — the Account Topic tab's own data RPC, not the cached
 * globalConfigs copy: a post-write refetch must reflect DB truth (incl. the
 * Phase 0 propagate-trigger cascade) without a backend restart. Per-account
 * detail state is tab-local (no Redux), like the funds dashboard hooks.
 */
export const useAccountTopics = (accountId: string | null | undefined) => {
  const recordIdString = accountId ? String(accountId) : null

  const [data, setData] = useState<AccountTopicsModel | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const cancelledRef = useRef(false)
  useEffect(() => {
    cancelledRef.current = false
    return () => {
      cancelledRef.current = true
    }
  }, [])

  const load = useCallback(async () => {
    if (!recordIdString) {
      setData(null)
      setLoading(false)
      setError(null)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const token = getAccessToken(store.getState().auth)
      const res = await fetchAccountTopics(recordIdString, token)
      if (cancelledRef.current) return
      if (res.error) setError(res.error)
      setData(res.data ?? null)
    } catch (e) {
      if (!cancelledRef.current) {
        setError(e instanceof Error ? e.message : 'Failed to load account topics')
      }
    } finally {
      if (!cancelledRef.current) setLoading(false)
    }
  }, [recordIdString])

  useEffect(() => {
    load()
  }, [load])

  const save = useCallback(
    async (payload: AccountTopicsSavePayload): Promise<boolean> => {
      if (!recordIdString) return false
      setSaving(true)
      setError(null)
      try {
        const token = getAccessToken(store.getState().auth)
        const res = await saveAccountTopics(recordIdString, payload, token)
        if (!res.success) {
          if (!cancelledRef.current) setError(res.error ?? 'Failed to save account topics')
          return false
        }
        // Re-pull authoritative state: the save fires the Phase 0 propagate
        // trigger and the contact⊆account intersection, so the grid must
        // reflect the post-write DB, not the optimistic local edits.
        await load()
        return true
      } catch (e) {
        if (!cancelledRef.current) {
          setError(e instanceof Error ? e.message : 'Failed to save account topics')
        }
        return false
      } finally {
        if (!cancelledRef.current) setSaving(false)
      }
    },
    [recordIdString, load]
  )

  return { data, loading, saving, error, refetch: load, save }
}
